import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { usePageHeader } from '../hooks/usePageHeader';

const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="border-b border-gray-200 last:border-b-0">
      <button
        className="w-full py-4 px-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
        onClick={onClick}
      >
        <span className="font-medium text-[#0A2342] pr-4">{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-[#FF6B35] flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="px-6 pb-4 text-gray-600 leading-relaxed">
          {answer}
        </div>
      )}
    </div>
  );
};

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const header = usePageHeader('faq', { heading: 'Frequently Asked Questions', subheading: 'Find answers to common questions about our products, services, and memberships.' });

  const faqSections = [
    {
      title: "Returns & Refunds",
      items: [
        {
          question: "Can I return an item I bought online?",
          answer: "Yes. If you purchased an item online, you may return it within 7 days of receiving it, provided it is unused, unopened, and in its original packaging."
        },
        {
          question: "Who pays for return shipping?",
          answer: "If you change your mind, return shipping costs are for your account. If the item is defective or incorrect, we'll assist in line with South African consumer law."
        },
        {
          question: "What if the item is defective?",
          answer: "If an item is defective, you may return it within 6 months. We'll assess the issue and may repair, replace, or refund the item in accordance with the Consumer Protection Act."
        },
        {
          question: "Can I return used or opened dive gear?",
          answer: "For hygiene and safety reasons, most used or opened dive gear cannot be returned unless it is defective. If you're unsure before purchasing, we recommend contacting us first so we can help you choose correctly."
        },
        {
          question: "Are membership fees refundable?",
          answer: "No. Membership fees are not refundable once activated, unless required by law."
        },
        {
          question: "How long do refunds take?",
          answer: "Approved refunds are processed as quickly as possible. The exact timing depends on your payment method and bank processing times."
        }
      ]
    },
    {
      title: "Training – FAQ for New Divers",
      items: [
        {
          question: "Do I need experience to start diving?",
          answer: "No experience is needed. Entry-level courses are designed for beginners and start with the basics."
        },
        {
          question: "How long does training take?",
          answer: "Training time varies depending on the course, your availability, and how quickly you progress. Diving is not rushed — training continues until you meet the required standards."
        },
        {
          question: "Is certification guaranteed once I pay?",
          answer: "No. Payment covers training and instruction, not guaranteed certification. Certification is issued only once all theory and practical skills are completed successfully and safely."
        },
        {
          question: "What if I struggle with certain skills?",
          answer: "That's completely normal. Some divers take longer than others. If needed, additional sessions can be arranged so you can progress at your own pace. Any additional costs are discussed upfront."
        },
        {
          question: "Do I need to be medically fit?",
          answer: "Yes. You must be medically fit to dive. You'll be asked to complete a medical questionnaire, and in some cases provide clearance from a medical professional."
        },
        {
          question: "What equipment do I need for training?",
          answer: "Some equipment is included depending on the course. Personal items such as masks, fins, and wetsuits are often recommended for comfort and fit. We'll guide you before training starts — there's no pressure to buy anything upfront."
        }
      ]
    },
    {
      title: "Membership – Club vs Pro Explained",
      items: [
        {
          question: "What is the Just Dive Club?",
          answer: "The Just Dive Club is an annual membership designed for divers who want better access and better value. It's ideal if you buy gear occasionally, plan to train further, or join dive trips from time to time."
        },
        {
          question: "What do Club members get?",
          answer: "Club members receive: Better pricing on new gear (20% discount), access to curated gear bundles, reduced training costs, priority access to trips, and access to member-only offers."
        },
        {
          question: "What is Just Dive Pro?",
          answer: "Just Dive Pro is a monthly membership for divers who want to dive more regularly. It's designed for people who buy gear more frequently, travel to dive, want one trip per year included, and prefer priority access and better long-term value."
        },
        {
          question: "What extra benefits do Pro members get?",
          answer: "In addition to all Club benefits, Pro members receive: Higher gear discounts (25%), one annual dive getaway, trip banking options, priority booking on limited trips, and better rates on trip extensions and family add-ons."
        },
        {
          question: "Can I upgrade from Club to Pro?",
          answer: "Yes. If you're already a Club member, you can upgrade to Pro at any time."
        },
        {
          question: "Do I have to use all the benefits?",
          answer: "No. Membership gives you access, not obligation. You use what you need, when you're ready."
        },
        {
          question: "Why is membership required for trips?",
          answer: "Membership allows trips to be planned fairly, priced properly, and run sustainably. It helps keep trips viable and enjoyable for everyone involved."
        }
      ]
    }
  ];

  const handleClick = (sectionIndex, itemIndex) => {
    const key = `${sectionIndex}-${itemIndex}`;
    setOpenIndex(openIndex === key ? null : key);
  };

  return (
    <div className="min-h-screen py-12 bg-[#FAFAFA]">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <div className="mb-8">
          <Link to="/" className="text-[#0A2342] hover:text-[#FF6B35] text-sm">
            ← Back to Home
          </Link>
        </div>
        
        <h1 className="text-4xl font-heading font-bold text-[#0A2342] mb-4">
          {header.heading}
        </h1>
        <p className="text-gray-600 mb-8">
          {header.subheading}
        </p>
        
        <div className="space-y-8">
          {faqSections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="bg-[#0A2342] px-6 py-4">
                <h2 className="text-xl font-heading font-semibold text-white">
                  {section.title}
                </h2>
              </div>
              <div>
                {section.items.map((item, itemIndex) => (
                  <FAQItem
                    key={itemIndex}
                    question={item.question}
                    answer={item.answer}
                    isOpen={openIndex === `${sectionIndex}-${itemIndex}`}
                    onClick={() => handleClick(sectionIndex, itemIndex)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-12 bg-[#FF6B35]/10 rounded-lg p-6 border border-[#FF6B35]/20">
          <h3 className="font-semibold text-[#0A2342] mb-2">Still have questions?</h3>
          <p className="text-gray-600">
            Contact us for more information. We're happy to help you find the right gear and get started with diving.
          </p>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
